import React from 'react';
import { Link } from 'react-router-dom';

import './LancelyComponent.css';

const caseStudyFacts = [
    { label: 'Role', value: 'Product concept, UX direction, requirements, prototype planning, and full-stack architecture.' },
    { label: 'Audience', value: 'Freelance photographers and videographers who manage clients, schedules, payments, contracts, and file delivery.' },
    { label: 'Stack', value: 'React, MongoDB, Amazon S3, Redis, Twilio, Stripe, and Figma.' }
];

const productCapabilities = [
    'Client and project records for active freelance work.',
    'Appointment scheduling with reminders and calendar-friendly workflows.',
    'Invoices, deposits, payment tracking, and dispute documentation.',
    'Model release and contract workflows for safer client handoff.',
    'File sharing with watermarked previews and clearer client selection.'
];

const requirements = [
    {
        title: 'Freelancer workspace',
        description: 'A central dashboard for projects, clients, schedule, payments, messages, and file delivery.'
    },
    {
        title: 'Client experience',
        description: 'A simpler way for clients to review shared work, select preferred files, sign forms, and pay without long email chains.'
    },
    {
        title: 'Business records',
        description: 'Structured records for invoices, communication, deposits, and contracts so work is easier to track if a job becomes messy.'
    },
    {
        title: 'File workflow',
        description: 'Upload, watermark, share, select, and deliver photo or video assets with fewer manual filename handoffs.'
    }
];

const risks = [
    'Keeping the product focused enough for solo freelancers while still covering payments, legal forms, files, and scheduling.',
    'Handling sensitive client files and business records with clear permissions and storage boundaries.',
    'Designing the desktop workflow first while leaving room for mobile communication and review flows.'
];

function LancelyComponent() {
    return (
        <div className="container lighter-background lancely-page">
            <div className="row">
                <article className="col col-12 col-expand text-left hero lancely-article">
                    <div className="spacer"></div>
                    <div className="project-page-nav">
                        <Link to="/portfolio" className="btn btn-link">Back to Portfolio</Link>
                    </div>

                    <header className="lancely-hero">
                        <p className="eyebrow">Product case study</p>
                        <h1 className="display-4 text-bold text-blue center-on-mobile">Lancely Co</h1>
                        <p className="lead text-blue">
                            I designed Lancely Co as a freelance management platform for photographers and videographers who need a cleaner way to manage bookings, payments, client communication, contracts, and project files.
                        </p>
                        <div className="img-responsive lancely-hero-image">
                            <img src="/assets/imgs/portfolio/lancely-co.png" alt="Lancely Co dashboard preview" />
                        </div>
                    </header>

                    <nav className="case-study-jump-links" aria-label="Lancely case study sections">
                        <a href="#project-summary">Summary</a>
                        <a href="#project-vision">Vision</a>
                        <a href="#project-requirements">Requirements</a>
                        <a href="#project-plan">Plan</a>
                        <a href="#wireframes">Wireframes</a>
                    </nav>

                    <section id="project-summary" className="case-study-section">
                        <h2 className="display-6 text-blue">Project Summary</h2>
                        <p>
                            The project came from a real workflow problem I kept running into while doing freelance photography: bookings, invoices, watermarked previews, file selection, release forms, and payment follow-up all lived in different tools. Lancely Co brings those steps into one product flow.
                        </p>
                        <dl className="case-study-facts">
                            {caseStudyFacts.map((fact) => (
                                <div key={fact.label}>
                                    <dt>{fact.label}</dt>
                                    <dd>{fact.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>

                    <section id="project-vision" className="case-study-section">
                        <h2 className="display-6 text-blue">Product Vision</h2>
                        <p>
                            Lancely Co is built around the idea that freelancers should not need to stitch together generic tools just to run a client project. The product direction focuses on reducing repetitive admin work and making the freelancer-client handoff easier to understand.
                        </p>
                        <ul className="case-study-list">
                            {productCapabilities.map((capability) => (
                                <li key={capability}>{capability}</li>
                            ))}
                        </ul>
                    </section>

                    <section id="project-requirements" className="case-study-section">
                        <h2 className="display-6 text-blue">Product Requirements</h2>
                        <p>
                            I mapped the product around the core entities a freelancer manages every day: clients, projects, appointments, files, invoices, forms, and payment status.
                        </p>
                        <div className="img-responsive requirements-diagram">
                            <img src="/assets/imgs/portfolio/lancely-co-picture1.png" alt="Lancely Co requirements diagram" />
                        </div>
                        <div className="requirements-list">
                            {requirements.map((requirement) => (
                                <section key={requirement.title}>
                                    <h3>{requirement.title}</h3>
                                    <p>{requirement.description}</p>
                                </section>
                            ))}
                        </div>
                    </section>

                    <section id="project-plan" className="case-study-section">
                        <h2 className="display-6 text-blue">Project Plan</h2>
                        <p>
                            The first phase focused on proving the desktop workflow: dashboard, projects, clients, scheduling, file review, and payment-related records. Mobile support would focus more on client communication, review, and lightweight updates.
                        </p>
                        <div className="case-study-columns">
                            <section>
                                <h3>Deliverables</h3>
                                <ul>
                                    <li>Responsive marketing and product surface.</li>
                                    <li>Desktop web app direction for heavier project management tasks.</li>
                                    <li>Mobile app direction for communication, reminders, and review flows.</li>
                                    <li>Figma wireframes for the main dashboard experience.</li>
                                </ul>
                            </section>
                            <section>
                                <h3>Risks planned around</h3>
                                <ul>
                                    {risks.map((risk) => (
                                        <li key={risk}>{risk}</li>
                                    ))}
                                </ul>
                            </section>
                        </div>
                    </section>

                    <section id="wireframes" className="case-study-section">
                        <h2 className="display-6 text-blue">Wireframes</h2>
                        <p>
                            The wireframes below show the desktop product direction for the primary dashboard and project-management flow.
                        </p>
                        <iframe title="Lancely Co wireframes" className="wireframe-embed" src="https://www.figma.com/embed?embed_host=share&url=https%3A%2F%2Fwww.figma.com%2Ffile%2FTVBVQNnAEvGE71oZwUdC9t%2FLancely-Co---Desktop---Final%3Fnode-id%3D0%253A1" allowFullScreen={true}></iframe>
                    </section>
                </article>
            </div>
        </div>
    )
}

export default LancelyComponent
